import { SharedSpinner } from '@components';
import { TechnicalCategories } from '@models';
import { TechnicalCategory } from '@services';
import { Button, Collapse } from 'antd';
import React from 'react';

interface IProps {
  category: string;
  loading: boolean;
  selectedCategoryId: string;
  categories: TechnicalCategory[];
  onCategorySelect: (id: string) => void;
  onCollapseChange: (key: string) => Promise<void>;
}

// Function to group items by their description
const groupByDescription = (items: TechnicalCategory[]) => {
  return items.reduce(
    (groups, item) => {
      const group = groups[item.description] || [];
      group.push(item);
      groups[item.description] = group;
      return groups;
    },
    {} as Record<string, TechnicalCategory[]>
  );
};

const TechnicalCategoryMenu: React.FC<IProps> = ({
  category,
  categories,
  selectedCategoryId,
  onCategorySelect,
  onCollapseChange,
  loading = false,
}) => {
  const collapseItems = TechnicalCategories.map((categoryName) => {
    const categoryItems = categories.filter((x) => x.category === categoryName);
    const isActive = category === categoryName; // currently expanded panel

    // Maintenance category: nested collapses by description
    if (categoryName === 'Maintenance') {
      const grouped = groupByDescription(categoryItems);

      const innerItems = Object.entries(grouped).map(([description, items]) => ({
        key: description,
        label: description,
        children:
          loading && isActive ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SharedSpinner />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {items.map((x) => (
                <Button
                  key={x.id}
                  type="text"
                  block
                  autoFocus={x.id === selectedCategoryId}
                  style={{
                    textAlign: 'left',
                    whiteSpace: 'break-spaces',
                    height: 'auto',
                    minHeight: 'auto',
                    padding: '8px 12px',
                    lineHeight: 1.4,
                    background: x.id === selectedCategoryId ? 'rgba(0,0,0,0.05)' : '',
                  }}
                  onClick={() => onCategorySelect(x.id)}
                >
                  {x.name}
                </Button>
              ))}
            </div>
          ),
      }));

      return {
        key: categoryName,
        label: categoryName,
        children: (
          <div style={{ maxHeight: 290, overflowY: 'auto' }}>
            <Collapse accordion items={innerItems} />
          </div>
        ),
      };
    }

    // Default category: simple button list
    return {
      key: categoryName,
      label: categoryName,
      children:
        loading && isActive ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SharedSpinner />
          </div>
        ) : (
          <div
            style={{
              maxHeight: 290,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {categoryItems.map((x) => (
              <Button
                key={x.id}
                type="text"
                block
                autoFocus={x.id === selectedCategoryId}
                style={{
                  textAlign: 'left',
                  whiteSpace: 'break-spaces',
                  height: 'auto',
                  minHeight: 'auto',
                  padding: '8px 12px',
                  lineHeight: 1.4,
                  background: x.id === selectedCategoryId ? 'rgba(0,0,0,0.05)' : '',
                }}
                onClick={() => onCategorySelect(x.id)}
              >
                {x.name}
              </Button>
            ))}
          </div>
        ),
    };
  });

  return (
    <Collapse
      accordion
      activeKey={category}
      expandIconPosition="end"
      onChange={(key) => onCollapseChange(String(key))}
      items={collapseItems}
    />
  );
};

export default TechnicalCategoryMenu;
